/**
 * Module loader.
 * - If a GLB URL is provided, fetches and parses via GLTFLoader.
 * - Otherwise, generates a procedural mesh that respects the module's bbox + sockets.
 *
 * This dual mode is what makes the configurator work out-of-the-box without
 * authoring time: ship the engine first, swap in real GLBs when ready.
 *
 * The procedural geometry is intentionally "furniture-grade" rather than raw
 * primitives: a low frame + plush seat cushion + back cushion + soft arms, all
 * built from RoundedBoxGeometry so edges read as upholstered, not lego-brick.
 */

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import type { ModuleDef } from "@/types";

// Draco decoder wired so Draco-compressed GLBs actually load (otherwise the
// loader throws and we silently fall back to procedural geometry). WebP textures
// (EXT_texture_webp) are handled natively by GLTFLoader in three r169.
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);
/** KTX2 (KHR_texture_basisu) support: GPU-compressed textures stay compressed in
 *  VRAM (~4x less than RGBA) and upload without a main-thread bitmap decode. The
 *  loader needs the renderer once (transcode-target detection) — Engine calls
 *  wireKtx2 at construction. Inert for GLBs with no KTX2 textures. */
let ktx2Wired = false;
export function wireKtx2(renderer: THREE.WebGLRenderer): void {
  if (ktx2Wired) return;
  const ktx2 = new KTX2Loader().setTranscoderPath("/basis/").detectSupport(renderer);
  gltfLoader.setKTX2Loader(ktx2);
  ktx2Wired = true;
}
// Insertion-ordered LRU. The Bungalow matrix is up to 128 baked GLBs (forme ×
// colorway × size), each carrying ~4K WebP maps that decode to tens-to-hundreds of
// MB of VRAM. An UNBOUNDED cache (the old behaviour) accumulates every GLB the user
// ever visits, so browsing the configurator eventually exhausts GPU memory → WebGL
// context loss → the browser reloads the tab (Alex's "switch size and the page
// crashes + reloads"). We keep only the most-recently-used MAX_CACHE_MODULES and
// dispose the rest, NEVER evicting a pinned (currently-displayed) URL.
const cache = new Map<string, THREE.Object3D>();
const MAX_CACHE_MODULES = 6;
/** Estimated DECODED texture bytes per cached URL (RGBA + mips). The file size
 *  lies: a 23 MB GLB of 4K WebP maps decodes to ~1-2 GB of RAM/VRAM. Measured
 *  2026-06-10: the 2-seater limestone alone = 9× 4096² maps ≈ 809 MB decoded —
 *  the count-based cap (6) allowed multi-GB accumulation → iOS Safari killed
 *  the tab (Alex's "viewer goes empty / page reloads itself" crashes). */
const cacheBytes = new Map<string, number>();
/** Decoded-bytes budget for EXTRA (non-displayed) cached models. The displayed
 *  model is a fixed cost we must carry; everything else is a speculative cache
 *  the budget bounds. Engine sets 0 on mobile (keep nothing beyond the live
 *  model), ~1.5 GB on desktop. */
let extrasBudgetBytes = 1_500_000_000;
export function setModuleCacheBudget(bytes: number): void {
  extrasBudgetBytes = Math.max(0, bytes);
  trimModuleCache();
}
/** URLs that must never be evicted because their clone is on screen right now.
 *  The engine pins the active GLB after each swap (see Engine.rebuildAssembly). */
const pinnedModules = new Set<string>();
/** Pin the GLB URLs currently displayed so the LRU never frees live geometry/maps
 *  (the assembly clones SHARE geometry + materials with the cached scene). */
export function setPinnedModules(urls: Array<string | undefined | null>): void {
  pinnedModules.clear();
  for (const u of urls) if (u) pinnedModules.add(u);
}
/** Move a URL to the most-recently-used end (insertion-ordered Map). */
function touchModule(url: string): void {
  const v = cache.get(url);
  if (v !== undefined) {
    cache.delete(url);
    cache.set(url, v);
  }
}
/** Estimate decoded GPU/RAM bytes of a scene's unique textures (RGBA ×1.33 mips). */
function estimateDecodedBytes(scene: THREE.Object3D): number {
  const seen = new Set<string>();
  let bytes = 0;
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const sm = m as THREE.MeshStandardMaterial | undefined;
      if (!sm) continue;
      for (const t of [sm.map, sm.normalMap, sm.roughnessMap, sm.metalnessMap, sm.aoMap, sm.emissiveMap]) {
        const img = t?.image as { width?: number; height?: number } | undefined;
        if (!t || !img || seen.has(t.uuid)) continue;
        seen.add(t.uuid);
        // Compressed (KTX2/UASTC → ASTC/BC7) stays ~1 byte/px in VRAM; classic
        // RGBA decodes to 4 bytes/px. Both get the ~1.33 mip-chain factor.
        const bpp = (t as THREE.Texture & { isCompressedTexture?: boolean }).isCompressedTexture ? 1 : 4;
        bytes += (img.width ?? 0) * (img.height ?? 0) * bpp * 1.33;
      }
    }
  });
  return bytes;
}
/** Evict least-recently-used, non-pinned modules until the EXTRAS fit the byte
 *  budget (and the count cap). Never touches pinned (displayed) entries, nor the
 *  most-recent entry — it may be mid-build, loaded but not yet pinned. Frees the
 *  GPU resources each evicted GLB owns (geometry + every baked texture map).
 *  The engine calls this right after re-pinning so the previous model's ~800 MB
 *  is released the moment a swap completes. */
export function trimModuleCache(): void {
  const keys = () => [...cache.keys()];
  const mru = keys().pop();
  const evictable = () => keys().filter((u) => !pinnedModules.has(u) && u !== mru);
  let list = evictable();
  let extrasTotal = list.reduce((a, u) => a + (cacheBytes.get(u) ?? 0), 0);
  while ((extrasTotal > extrasBudgetBytes || cache.size > MAX_CACHE_MODULES) && list.length > 0) {
    const victim = list[0]; // iteration order = LRU first
    const scene = cache.get(victim);
    cache.delete(victim);
    extrasTotal -= cacheBytes.get(victim) ?? 0;
    cacheBytes.delete(victim);
    if (scene) disposeCachedScene(scene);
    list = evictable();
  }
  publishCacheStats();
}
/** Publish cache stats for probes/diagnostics: entry count + total estimated
 *  decoded bytes (INCLUDING the pinned/displayed model — this approximates the
 *  resident texture memory, the thing that kills the iOS tab). */
function publishCacheStats(): void {
  if (typeof window === "undefined") return;
  let bytes = 0;
  for (const b of cacheBytes.values()) bytes += b;
  const w = window as unknown as { __NEOVAA_MODULE_CACHE__?: number; __NEOVAA_CACHE_BYTES__?: number; __NEOVAA_CACHE_BYTES_PEAK__?: number };
  w.__NEOVAA_MODULE_CACHE__ = cache.size;
  w.__NEOVAA_CACHE_BYTES__ = bytes;
  w.__NEOVAA_CACHE_BYTES_PEAK__ = Math.max(w.__NEOVAA_CACHE_BYTES_PEAK__ ?? 0, bytes);
}
/** In-flight GLB loads, keyed by URL. Ensures concurrent loads of the SAME url
 *  (e.g. a predictive prefetch racing a real assembly load) share ONE network
 *  fetch + parse instead of two. Populated before the await, cleared on settle. */
const pending = new Map<string, Promise<THREE.Object3D>>();

/* Verifier instrumentation — tracked so Playwright can assert real GLB use
 * (vs procedural fallback). See e2e/quaternius-living-room.spec.ts. */
declare global {
  interface Window {
    __NEOVAA_GLTF_LOADS__?: number;
    __NEOVAA_GLTF_FALLBACKS__?: number;
    /** Set when a module that DECLARED a GLB url failed to load it and we fell
     *  back to procedural geometry. A declared-GLB failure is an incident
     *  (404 / Draco decoder / corrupt asset), not a design choice — the
     *  verifier asserts this stays null. Records {moduleId, url, error}. */
    __NEOVAA_GLB_ERRORS__?: Array<{ moduleId: string; url: string; error: string }>;
  }
}

function bumpCounter(key: "__NEOVAA_GLTF_LOADS__" | "__NEOVAA_GLTF_FALLBACKS__"): void {
  if (typeof window === "undefined") return;
  window[key] = (window[key] ?? 0) + 1;
}

/**
 * preload-warm (loading trick #5): start downloading + initialising the Draco
 * decoder (WASM/worker, on gstatic) BEFORE the first GLB needs decoding, so the
 * decoder is ready when the heavy GLB bytes arrive instead of starting its own
 * download only at decode time. Free, no asset/quality change. Idempotent —
 * three's DRACOLoader.preload guards re-init. Best-effort; never blocks boot.
 */
export function warmDracoDecoder(): void {
  try {
    dracoLoader.preload();
  } catch {
    /* best-effort warmup — a failure here is recovered by the normal load path */
  }
}

export interface LoadedModule {
  /** Cloned scene object. Safe to add to scene multiple times. */
  object: THREE.Object3D;
  /** Source definition for socket lookup etc. */
  def: ModuleDef;
  /** True when this module DECLARED a glb url but it failed to load, so `object`
   *  is a procedural placeholder, not the real asset. The engine uses this to
   *  refuse displaying a degraded build (no white-box swap). Absent/false means
   *  either the real GLB loaded, or the module is procedural by design (no glb). */
  failed?: boolean;
}

/* ── Shared module dimensions ───────────────────────────────────────────────
 * Every module aligns to these so a multi-module assembly reads as one sofa.
 * The frame bottom sits at y=0; legs hang below to y=-LEG_H. */
const SEAT_DEPTH = 0.98; // Z extent of a regular / arm / corner module
const CHAISE_DEPTH = 1.5; // Z extent of a chaise module
const BASE_H = 0.3; // upholstered frame height
const SEAT_CUSHION_H = 0.17;
const BACK_H = 0.5;
const BACK_THICK = 0.24;
const ARM_W = 0.17;
const ARM_H = 0.56;
const LEG_H = 0.14;
const LEG_INSET = 0.13;

const BODY_R = 0.05; // rounded-edge radius for frame / arms
const CUSHION_R = 0.07; // rounded-edge radius for cushions — plusher

// Derived Y centres
const BASE_CY = BASE_H / 2;
const SEAT_CY = BASE_H + SEAT_CUSHION_H / 2;
const BACK_CY = BASE_H + BACK_H / 2;

export async function loadModule(def: ModuleDef): Promise<LoadedModule> {
  if (def.glb) {
    try {
      const obj = await loadGLB(def.glb);
      const cloned = obj.clone(true);
      cloned.userData.fromGLB = true;
      cloned.userData.moduleId = def.id;
      cloned.userData.glbUrl = def.glb;
      if (typeof def.scale === "number" && def.scale > 0 && def.scale !== 1) {
        cloned.scale.setScalar(def.scale);
      }
      cloned.traverse((o) => {
        o.userData.fromGLB = true;
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh) {
          // Hide config-declared toggle variants (e.g. a lamp's emissive "-on"
          // shade/bulb meshes that ship in the GLB but should NOT render — they
          // z-fight the off-state and blow out the shade). Match mesh + material
          // name(s) against any hiddenParts substring.
          if (def.hiddenParts && def.hiddenParts.length > 0) {
            const meshName = (mesh.name || "").toLowerCase();
            const matNames = (Array.isArray(mesh.material) ? mesh.material : [mesh.material])
              .map((m) => (m?.name || "").toLowerCase());
            const hide = def.hiddenParts.some((p) => {
              const needle = p.toLowerCase();
              return meshName.includes(needle) || matNames.some((n) => n.includes(needle));
            });
            if (hide) {
              o.visible = false;
              return; // skip shadow flags + material tagging for a hidden mesh
            }
          }
          o.castShadow = true;
          o.receiveShadow = true;
          // Tag GLB materials so Materials.applyFabric / applyLeg can recolor them.
          // Convention: material named "*leg*" → leg, everything else → fabric.
          const tag = (m: THREE.Material) => {
            const name = (m.name || "").toLowerCase();
            if (name.includes("leg")) {
              m.userData.isLeg = true;
              return;
            }
            m.userData.isFabric = true;
            // Remember the GLB's baked weave map ONCE, so a swap back to a baked
            // fabric (no textureUrl) restores it instead of keeping the previous
            // runtime weave. The material is shared with the cache, so this sticks.
            const sm = m as THREE.MeshStandardMaterial;
            if (sm.map && m.userData.bakedMap === undefined) m.userData.bakedMap = sm.map;
          };
          const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
          if (Array.isArray(mat)) mat.forEach(tag);
          else if (mat) tag(mat);
        }
      });
      return { object: cloned, def };
    } catch (e) {
      // A module that DECLARED a GLB url but failed to load it is an incident,
      // not a design choice: 404, Draco decoder unreachable, corrupt/garbage
      // asset, CORS. We still fall back to procedural so the configurator does
      // not go fully blank, but we surface the failure LOUDLY (console.error +
      // a window flag the verifier asserts) so it can never hide in prod.
      bumpCounter("__NEOVAA_GLTF_FALLBACKS__");
      const message = e instanceof Error ? e.message : String(e);
      console.error(
        `[Neovaa] GLB load FAILED for module "${def.id}" (${def.glb}) — ` +
          `showing procedural placeholder instead of the real asset. ` +
          `This is a production incident, not a design fallback.`,
        e,
      );
      if (typeof window !== "undefined") {
        (window.__NEOVAA_GLB_ERRORS__ ??= []).push({
          moduleId: def.id,
          url: def.glb,
          error: message,
        });
      }
      // Return procedural as a last-ditch so one bad asset can't crash the whole
      // build, but MARK it failed so the engine refuses to display this degraded
      // build (no white-box swap) and keeps the cover / previous model instead.
      return { object: generateProcedural(def), def, failed: true };
    }
  }
  const obj = generateProcedural(def);
  return { object: obj, def };
}

function loadGLB(url: string): Promise<THREE.Object3D> {
  const cached = cache.get(url);
  if (cached) {
    touchModule(url); // mark MRU so an active GLB is never the LRU eviction victim
    return Promise.resolve(cached);
  }
  const existing = pending.get(url);
  if (existing) return existing;
  const promise = doLoadGLB(url);
  pending.set(url, promise);
  // Free the in-flight slot once settled: success leaves the URL in `cache`,
  // failure leaves it absent so a later call retries. The side `.catch` keeps
  // THIS bookkeeping chain from raising an unhandled rejection — the original
  // `promise` still rejects for the awaiting caller (loadModule / prefetch) to
  // handle exactly as before.
  promise.catch(() => {}).finally(() => pending.delete(url));
  return promise;
}

/** Per-URL download progress listeners (0..1, or -1 when the response carries no
 *  content-length so the ratio is unknowable). The UI's HD indicator subscribes
 *  for the full-model URL during a progressive load — REAL bytes on the wire,
 *  never a fake spinner (Alex 2026-06-12: "calibré au temps réel"). */
const progressListeners = new Map<string, Set<(p: number) => void>>();

export function onModuleProgress(url: string, cb: (p: number) => void): () => void {
  let set = progressListeners.get(url);
  if (!set) {
    set = new Set();
    progressListeners.set(url, set);
  }
  set.add(cb);
  return () => {
    const s = progressListeners.get(url);
    if (!s) return;
    s.delete(cb);
    if (s.size === 0) progressListeners.delete(url);
  };
}

async function doLoadGLB(url: string): Promise<THREE.Object3D> {
  bumpCounter("__NEOVAA_GLTF_LOADS__");
  const markStart = `glb-load-start:${url}`;
  const markEnd = `glb-load-end:${url}`;
  if (typeof performance !== "undefined") performance.mark(markStart);
  const gltf = await gltfLoader.loadAsync(url, (e) => {
    const set = progressListeners.get(url);
    if (!set || set.size === 0) return;
    const p = e.lengthComputable && e.total > 0 ? Math.min(1, e.loaded / e.total) : -1;
    set.forEach((cb) => cb(p));
  });
  if (typeof performance !== "undefined") {
    performance.mark(markEnd);
    try {
      performance.measure(`glb-load:${url}`, markStart, markEnd);
    } catch {
      /* mark missing — ignore */
    }
  }
  cache.set(url, gltf.scene);
  cacheBytes.set(url, estimateDecodedBytes(gltf.scene));
  trimModuleCache(); // bound RAM/VRAM: drop the oldest non-pinned GLBs past the byte budget
  return gltf.scene;
}

/**
 * Predictive prefetch: warm the caches for a GLB without instantiating it.
 * Opportunistic — resolves whether the load succeeds or fails (a real load later
 * surfaces failures through the normal loadModule path).
 *
 * Two depths:
 *  - parse:true (desktop) — full parse into the module cache, so a switch is a
 *    cache hit (0 network, 0 decode). Costs decoded RAM/VRAM, bounded by the
 *    byte budget.
 *  - parse:false (mobile) — fetch only: warms the browser HTTP cache so a later
 *    switch is a fast local read, but keeps ZERO decoded bytes in memory. On
 *    phones, parse-prefetch was stacking ~800 MB models in the background and
 *    killing the tab.
 */
export function prefetchModuleGLB(url: string, opts: { parse?: boolean } = {}): Promise<void> {
  if (opts.parse === false) {
    if (cache.has(url) || pending.has(url)) return Promise.resolve();
    return fetch(url).then(
      (r) => r.arrayBuffer().then(() => {}),
      () => {},
    ).catch(() => {});
  }
  return loadGLB(url).then(
    () => {},
    () => {},
  );
}

/**
 * Generate a procedural sofa module. The kind controls the silhouette, the
 * width drives layout maths. All upholstered parts are flagged `isFabric` and
 * all legs `isLeg` so the Materials module can recolour them.
 */
function generateProcedural(def: ModuleDef): THREE.Object3D {
  const kind = def.proceduralKind ?? "box";
  const w = def.width;
  const color = new THREE.Color(def.proceduralColor ?? "#cdc6b6");

  const group = new THREE.Group();
  group.name = `module:${def.id}`;
  group.userData.moduleId = def.id;

  const fabricMat = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.82,
    metalness: 0,
  });
  fabricMat.userData.isFabric = true;

  const legMat = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a,
    roughness: 0.35,
    metalness: 0.4,
  });
  legMat.userData.isLeg = true;

  // Socket empties (invisible attach points).
  for (const s of def.sockets) {
    const empty = new THREE.Object3D();
    empty.name = `socket:${s.name}`;
    empty.position.set(s.position.x, s.position.y, s.position.z);
    if (s.rotation) {
      empty.rotation.set(
        (s.rotation.x * Math.PI) / 180,
        (s.rotation.y * Math.PI) / 180,
        (s.rotation.z * Math.PI) / 180,
      );
    }
    group.add(empty);
  }

  switch (kind) {
    case "box":
      buildSeatModule(group, fabricMat, legMat, w, SEAT_DEPTH);
      break;
    case "wedge":
      buildArmModule(group, fabricMat, legMat, w, def);
      break;
    case "chaise":
      buildChaiseModule(group, fabricMat, legMat, w);
      break;
    case "cylinder": {
      const seat = new THREE.Mesh(new THREE.CylinderGeometry(w / 2, w / 2, 0.34, 32), fabricMat);
      seat.position.y = BASE_H - 0.04;
      tagFabric(seat);
      group.add(seat);
      addLegs(group, legMat, w, SEAT_DEPTH);
      break;
    }
    case "cone": {
      const cone = new THREE.Mesh(new THREE.ConeGeometry(w / 2, 0.55, 32), fabricMat);
      cone.position.y = 0.27;
      tagFabric(cone);
      group.add(cone);
      break;
    }
  }

  return group;
}

/** Regular / corner section: frame + plush seat cushion + back cushion. */
function buildSeatModule(
  group: THREE.Group,
  fabricMat: THREE.Material,
  legMat: THREE.Material,
  w: number,
  depth: number,
): void {
  const base = roundedBox(w, BASE_H, depth, BODY_R, fabricMat);
  base.position.set(0, BASE_CY, 0);
  group.add(base);

  const seat = roundedBox(w * 0.97, SEAT_CUSHION_H, depth - BACK_THICK, CUSHION_R, fabricMat);
  seat.position.set(0, SEAT_CY, BACK_THICK / 2);
  group.add(seat);

  const back = roundedBox(w * 0.97, BACK_H, BACK_THICK, CUSHION_R, fabricMat);
  back.position.set(0, BACK_CY, -depth / 2 + BACK_THICK / 2);
  group.add(back);

  addLegs(group, legMat, w, depth);
}

/** Arm (end-cap) module: full-width frame, a soft arm on the outer edge. */
function buildArmModule(
  group: THREE.Group,
  fabricMat: THREE.Material,
  legMat: THREE.Material,
  w: number,
  def: ModuleDef,
): void {
  // A left-arm module exposes only a "right" socket (it caps the left end).
  const hasRight = def.sockets.some((s) => s.name === "right");
  const hasLeft = def.sockets.some((s) => s.name === "left");
  const isLeftArm = hasRight && !hasLeft;
  const sign = isLeftArm ? 1 : -1; // +1 => arm on -X side, seat opens to +X

  const base = roundedBox(w, BASE_H, SEAT_DEPTH, BODY_R, fabricMat);
  base.position.set(0, BASE_CY, 0);
  group.add(base);

  // Arm block on the outer edge, slightly proud of the frame depth.
  const armX = -sign * (w / 2 - ARM_W / 2);
  const arm = roundedBox(ARM_W, ARM_H, SEAT_DEPTH, BODY_R, fabricMat);
  arm.position.set(armX, ARM_H / 2, 0);
  group.add(arm);

  // Inner seat + back so the arm module flows into its neighbour.
  const innerW = w - ARM_W;
  if (innerW > 0.12) {
    const innerX = sign * (ARM_W / 2);
    const seat = roundedBox(innerW * 0.96, SEAT_CUSHION_H, SEAT_DEPTH - BACK_THICK, CUSHION_R, fabricMat);
    seat.position.set(innerX, SEAT_CY, BACK_THICK / 2);
    group.add(seat);

    const back = roundedBox(innerW * 0.96, BACK_H, BACK_THICK, CUSHION_R, fabricMat);
    back.position.set(innerX, BACK_CY, -SEAT_DEPTH / 2 + BACK_THICK / 2);
    group.add(back);
  }

  // End-cap: legs only on the arm (outer) side.
  addLegs(group, legMat, w, SEAT_DEPTH, isLeftArm ? "left" : "right");
}

/** Chaise lounge: long deep seat, low back cushion at the rear end only. */
function buildChaiseModule(
  group: THREE.Group,
  fabricMat: THREE.Material,
  legMat: THREE.Material,
  w: number,
): void {
  const base = roundedBox(w, BASE_H, CHAISE_DEPTH, BODY_R, fabricMat);
  base.position.set(0, BASE_CY, 0);
  group.add(base);

  const seat = roundedBox(w * 0.97, SEAT_CUSHION_H, CHAISE_DEPTH - BACK_THICK, CUSHION_R, fabricMat);
  seat.position.set(0, SEAT_CY, BACK_THICK / 2);
  group.add(seat);

  // Lower back cushion (a chaise reclines, the back is shorter).
  const chaiseBackH = BACK_H * 0.62;
  const back = roundedBox(w * 0.97, chaiseBackH, BACK_THICK, CUSHION_R, fabricMat);
  back.position.set(0, BASE_H + chaiseBackH / 2, -CHAISE_DEPTH / 2 + BACK_THICK / 2);
  group.add(back);

  addLegs(group, legMat, w, CHAISE_DEPTH);
}

/**
 * Tapered legs along a w × depth footprint.
 * `mode` controls which side gets legs — arm (end-cap) modules only need legs
 * on their outer edge so a multi-module sofa doesn't grow a cluster of feet at
 * every join.
 */
function addLegs(
  group: THREE.Group,
  legMat: THREE.Material,
  w: number,
  depth: number,
  mode: "all" | "left" | "right" = "all",
): void {
  const lx = Math.max(0.05, w / 2 - LEG_INSET);
  const lz = Math.max(0.05, depth / 2 - LEG_INSET);
  const xs = mode === "left" ? [-lx] : mode === "right" ? [lx] : [-lx, lx];
  for (const x of xs) {
    for (const z of [lz, -lz]) {
      const geo = new THREE.CylinderGeometry(0.036, 0.024, LEG_H, 16);
      const leg = new THREE.Mesh(geo, legMat);
      leg.position.set(x, -LEG_H / 2, z);
      leg.castShadow = true;
      leg.userData.isLeg = true;
      group.add(leg);
    }
  }
}

/** Build a rounded-edge upholstered box, tagged as fabric + shadow-enabled. */
function roundedBox(
  w: number,
  h: number,
  d: number,
  radius: number,
  mat: THREE.Material,
): THREE.Mesh {
  const safeR = Math.min(radius, Math.min(w, h, d) / 2 - 0.001);
  const geo = new RoundedBoxGeometry(w, h, d, 3, Math.max(0.005, safeR));
  const mesh = new THREE.Mesh(geo, mat);
  tagFabric(mesh);
  return mesh;
}

function tagFabric(mesh: THREE.Mesh): void {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData.isFabric = true;
}

/** Dispose the GPU resources a cached GLB scene owns. Runtime-swapped fabric
 *  maps live in Materials' textureCache (freed by clearTextureCache); here we
 *  free the GLB's own baked maps + geometry + material. */
function disposeCachedScene(scene: THREE.Object3D): void {
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
    const disposeOne = (m: THREE.Material) => {
      const sm = m as THREE.MeshStandardMaterial;
      sm.map?.dispose();
      sm.normalMap?.dispose();
      sm.roughnessMap?.dispose();
      sm.metalnessMap?.dispose();
      sm.aoMap?.dispose();
      sm.emissiveMap?.dispose();
      // The baked weave kept for fabric restore: free it too (it may be off-map
      // right now if a runtime weave is applied), so nothing orphans at teardown.
      const baked = m.userData?.bakedMap as THREE.Texture | undefined;
      if (baked && baked !== sm.map) baked.dispose();
      m.dispose();
    };
    if (Array.isArray(mat)) mat.forEach(disposeOne);
    else if (mat) disposeOne(mat);
  });
}

/**
 * Free a SINGLE cached module by url, disposing its GPU resources immediately.
 * Used by the progressive-LOD swap: the moment the full GLB is on screen, the
 * light proxy (X.lod.glb) is no longer needed, so we drop it deterministically
 * instead of waiting for the LRU/byte budget to evict it. Honors the LOD memory
 * contract (proxy + full are NEVER both kept). No-op if the url isn't cached or
 * is currently pinned (displayed) — a pinned url is live geometry, never freed
 * here. Returns true if it actually evicted something.
 */
export function evictModule(url: string): boolean {
  if (pinnedModules.has(url)) return false;
  const scene = cache.get(url);
  if (!scene) return false;
  cache.delete(url);
  cacheBytes.delete(url);
  pending.delete(url);
  disposeCachedScene(scene);
  if (typeof window !== "undefined") {
    (window as unknown as { __NEOVAA_MODULE_CACHE__?: number }).__NEOVAA_MODULE_CACHE__ = cache.size;
  }
  return true;
}

/**
 * Free every cached module. Called at engine teardown (destroy). Because
 * disposeAssembly no longer disposes GLB geometry/materials per rebuild (they're
 * shared with this cache — see finding #2), this is the single point that frees
 * their GPU resources, so teardown stays leak-free.
 */
export function clearModuleCache(): void {
  for (const scene of cache.values()) disposeCachedScene(scene);
  cache.clear();
  cacheBytes.clear();
  pending.clear();
}
